import React, { useRef } from 'react';

import { convertLength, convertWeight } from '../../../../../helpers/conversion';
import { useScrollToElement } from '../../../../../helpers/hooks/useScrollToElement';
import { getTripUrl } from '../../../../../helpers/urls';
import { CatchMap } from '../../../../CatchMap/CatchMap';
import { TripInfoBar } from '../../../../TripInfoBar/TripInfoBar';

import { CatchConditions } from './CatchConditions';
import { CatchDetailsSection } from './CatchDetailsSection';
import { CatchDetailsTable } from './CatchDetailsTable';
import { CatchGearCards } from './CatchGearCards';
import { CatchDetails as CatchDetailsData } from './useCatchDetails';

interface CatchDetailsProps {
  catchDetails: CatchDetailsData;
  scrollSelector?: string;
}

export const CatchDetails = ({ catchDetails, scrollSelector }: CatchDetailsProps) => {
  const catchDetailsReference = useRef<HTMLDivElement>(null);
  const { catchGear, trip, currentUser, catchConditions, catchPost } = catchDetails;
  const fishingWater = catchPost?.fishingWater;
  const hasFishingWater = !!fishingWater;
  const hasExactPosition = !!catchPost?.hasExactPosition;
  const { isPremium: isCurrentUserPremium, user: currentUserData } = currentUser;
  const latitude = catchConditions?.latitude ?? undefined;
  const locationPrivacy = catchPost?.locationPrivacy;
  const longitude = catchConditions?.longitude ?? undefined;
  const postUser = catchPost?.user;
  const ownedByCurrentUser = postUser?._id === currentUserData?._id;
  const fishingWaterName = catchPost?.fishingWater?.displayName;
  const CatchCharacteristicsSectionTitle = 'Catch details';
  const catchAndRelease = catchPost?.catchAndRelease;
  const catchDate = catchPost?.caughtAtGmt || catchPost?.createdAt;
  const length = catchPost?.length;
  const weight = catchPost?.weight;
  const convertedLength = length ? convertLength(length, currentUserData) : '–';
  const convertedWeight = weight ? convertWeight(weight, currentUserData, false, true) : '–';
  const fishingMethodName = catchPost?.fishingMethod?.displayName;
  const speciesId = catchPost?.species?._id;
  const speciesName = catchPost?.species?.displayName;
  const CatchConditionsSectionTitle = 'Catch conditions';
  const hasCatchGear = catchGear.length > 0;
  const CatchProductsSectionTitle = 'Gear used';
  const hasTrip = !!trip;
  const tripCatchesCount = trip?.catches.totalCount ?? 0;
  const tripUrl = getTripUrl(trip?.id || '');

  useScrollToElement(catchDetailsReference, scrollSelector);

  return (
    <div className="overflow-y-scroll scrollbar-none" ref={catchDetailsReference}>
      {hasFishingWater && (
        <CatchMap
          hasExactPosition={hasExactPosition}
          isPro={isCurrentUserPremium}
          latitude={latitude}
          locationPrivacy={locationPrivacy}
          longitude={longitude}
          ownedByCurrentUser={ownedByCurrentUser}
          waterDetails={fishingWater}
          waterName={fishingWaterName}
          source="catch-viewer-map"
        />
      )}
      <div className="px-4 py-6">
        <CatchDetailsSection title={CatchCharacteristicsSectionTitle}>
          <CatchDetailsTable
            catchAndRelease={catchAndRelease}
            catchDate={catchDate}
            convertedLength={convertedLength}
            convertedWeight={convertedWeight}
            fishingMethod={fishingMethodName}
            speciesId={speciesId}
            speciesName={speciesName}
          />
        </CatchDetailsSection>
        <CatchDetailsSection title={CatchConditionsSectionTitle}>
          <CatchConditions
            catchConditions={catchConditions}
            currentUser={currentUser}
            postUser={postUser}
          />
        </CatchDetailsSection>
        {hasCatchGear && (
          <CatchDetailsSection
            className="mb-0"
            id="catch-products"
            title={CatchProductsSectionTitle}
          >
            <CatchGearCards catchGear={catchGear} />
          </CatchDetailsSection>
        )}
        {hasTrip && (
          <CatchDetailsSection>
            <TripInfoBar catchesCount={tripCatchesCount} url={tripUrl} />
          </CatchDetailsSection>
        )}
      </div>
    </div>
  );
};
